// Sprint-15 — team-watch HTTP wrapper.
//
// Thin axios layer over /api/v1 watch endpoints. Every call goes through the
// shared `api` instance so the bearer-token interceptor + 401 redirect keep
// working without ceremony. The store (watchStore.ts) owns optimistic state;
// these calls are just transport.

import { api } from './apiClient';

export interface WatchRecord {
  id: string;
  user_id: string;
  guide_id: string;
  watched_at: string;
  watch_progress_pct: number;
}

export interface Watcher {
  id: string;
  user_id: string;
  watched_at: string;
  watch_progress_pct: number;
  watcher: { id: string; email: string; display_name?: string | null } | null;
}

export const watchesApi = {
  /**
   * Mark a guide as watched. Idempotent — calling twice is fine; the server
   * refreshes watched_at on each call so "Watched · 2h ago" always reads the
   * latest viewing. Returns the persisted row so callers can echo watched_at
   * back into their store without a follow-up GET.
   */
  async markWatched(guideId: string, progress_pct?: number): Promise<WatchRecord> {
    const r = await api.post(`/api/v1/guides/${guideId}/watch`,
      progress_pct == null ? {} : { progress_pct });
    return r.data.watch as WatchRecord;
  },

  /**
   * Remove this user's watch on a guide. Server treats missing rows as no-op
   * so the UI doesn't need to guard against "you weren't watching anyway".
   */
  async unwatch(guideId: string): Promise<void> {
    await api.delete(`/api/v1/guides/${guideId}/watch`);
  },

  /**
   * Org-wide count of guides the current user hasn't watched yet. Excludes
   * their own captures and archived rows server-side. Drives the top-nav badge.
   */
  async unwatchedCount(): Promise<number> {
    const r = await api.get('/api/v1/me/unwatched-count');
    return Number(r.data?.count) || 0;
  },

  /**
   * Avatar-stack data for a single guide — newest watcher first.
   */
  async listWatchers(guideId: string): Promise<Watcher[]> {
    const r = await api.get(`/api/v1/guides/${guideId}/watches`);
    return (r.data.watches || []) as Watcher[];
  },
};
